import { theme } from '@procurify/ui-theme'
import { type IntlShape, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import {
  makeSelectOrganizationalSegmentCustomLabels,
  makeSelectOrganizationalSegmentLabel,
  PRIMARY_ORG_CATEGORY_LABELS,
  SECONDARY_ORG_CATEGORY_LABELS,
} from '@webapp/core/stores/global'

const richTextHtmlHelpers = {
  strong: (str) => <strong>{str}</strong>,
  br: <br />,
  asterisk: <span style={{ color: theme.colors.danger }}>*</span>,
  italic: (str) => <i>{str}</i>,
}

export const useProcurifyIntl = (): IntlShape => {
  const { formatMessage, ...defaultIntl } = useIntl()

  const enableOrganizationalCategoryLabels = useSelector(
    makeSelectOrganizationalSegmentCustomLabels()
  )

  const childOrganizationalCategoryLabel = useSelector(
    makeSelectOrganizationalSegmentLabel()
  )

  const singularSecondaryOrgCategoryLabel = enableOrganizationalCategoryLabels
    ? childOrganizationalCategoryLabel.department.singular
    : SECONDARY_ORG_CATEGORY_LABELS.SINGULAR
  const pluralSecondaryOrgCategoryLabel = enableOrganizationalCategoryLabels
    ? childOrganizationalCategoryLabel.department.plural
    : SECONDARY_ORG_CATEGORY_LABELS.PLURAL
  const singularPrimaryOrgCategoryLabel = enableOrganizationalCategoryLabels
    ? childOrganizationalCategoryLabel.location.singular
    : PRIMARY_ORG_CATEGORY_LABELS.SINGULAR
  const pluralPrimaryOrgCategoryLabel = enableOrganizationalCategoryLabels
    ? childOrganizationalCategoryLabel.location.plural
    : PRIMARY_ORG_CATEGORY_LABELS.PLURAL

  const toMidSentenceLabel = (label: string) =>
    enableOrganizationalCategoryLabels ? label : label.toLowerCase()

  const procurifyFormatMessage: IntlShape['formatMessage'] = (
    descriptor,
    values
  ) => {
    return formatMessage(descriptor, {
      ...values,
      ...richTextHtmlHelpers,
      singularSecondaryOrgCategoryLabel,
      pluralSecondaryOrgCategoryLabel,
      singularPrimaryOrgCategoryLabel,
      pluralPrimaryOrgCategoryLabel,
      // Mid-sentence variants ("14 active locations"). Only the default
      // Procurify nouns are lowercased; a customer's custom labels are
      // rendered exactly as configured so proper nouns are never altered.
      singularSecondaryOrgCategoryLabelLowercase: toMidSentenceLabel(
        singularSecondaryOrgCategoryLabel
      ),
      pluralSecondaryOrgCategoryLabelLowercase: toMidSentenceLabel(
        pluralSecondaryOrgCategoryLabel
      ),
      singularPrimaryOrgCategoryLabelLowercase: toMidSentenceLabel(
        singularPrimaryOrgCategoryLabel
      ),
      pluralPrimaryOrgCategoryLabelLowercase: toMidSentenceLabel(
        pluralPrimaryOrgCategoryLabel
      ),
    })
  }

  return {
    ...defaultIntl,
    formatMessage: procurifyFormatMessage,
  }
}
